<template>
	<PCard class="project-page">
		<template #title>
			<div class="flex align-items-center justify-content-between flex-wrap gap-2">
				<div>
					<WTitle text="Project" />
					<WSubTitle text="Commissioned plant identity and contract power ratings" />
				</div>
			</div>
		</template>
		<template #content>
			<section class="project-hero">
				<div class="project-hero__icon">
					<i class="mdi mdi-solar-power"></i>
				</div>
				<div class="project-hero__copy">
					<div class="project-hero__name">Solar power plant</div>
					<p class="project-hero__lead">
						Key contractual milestones and installed capacity used by
						tariff and deemed-energy calculations.
					</p>
				</div>
				<div class="project-hero__status">
					<span class="project-hero__status-label">Status</span>
					<span class="project-hero__status-value">In commercial operation</span>
				</div>
			</section>

			<section class="project-section">
				<h2 class="project-section__title">Milestones</h2>
				<p class="project-section__lead">
					Dates that define the transition from initial to full commercial operation.
				</p>
				<div class="project-grid">
					<article
						:key="item.key"
						class="project-fact"
						v-for="item in milestones"
					>
						<div
							:class="['project-fact__icon', `project-fact__icon--${item.tone}`]"
						>
							<i :class="item.icon"></i>
						</div>
						<div class="project-fact__body">
							<span class="project-fact__label">{{ item.label }}</span>
							<span class="project-fact__value">{{ item.value }}</span>
							<span
								v-if="item.hint"
								class="project-fact__hint"
							>{{ item.hint }}</span>
						</div>
					</article>
				</div>
			</section>

			<section class="project-section">
				<h2 class="project-section__title">Installed capacity</h2>
				<p class="project-section__lead">
					AC power ratings used for pre-COD scaling (IPNOM / PNOM) in settlement.
				</p>
				<div class="project-grid">
					<article
						:key="item.key"
						class="project-fact"
						v-for="item in capacity"
					>
						<div
							:class="['project-fact__icon', `project-fact__icon--${item.tone}`]"
						>
							<i :class="item.icon"></i>
						</div>
						<div class="project-fact__body">
							<span class="project-fact__label">{{ item.label }}</span>
							<span class="project-fact__value mono">{{ item.value }}</span>
							<span
								v-if="item.hint"
								class="project-fact__hint"
							>{{ item.hint }}</span>
						</div>
					</article>
				</div>
			</section>
		</template>
	</PCard>
</template>

<script lang="ts">
	import { defineComponent } from 'vue'

	export default defineComponent({
		name: 'ProjectDetailsView',

		data() {
			return {
				milestones: [
					{
						key: 'ico',
						label: 'Initial Commercial Operation',
						value: '01 Sep 2024',
						hint: 'Start of initial commercial operation (ICO)',
						icon: 'mdi mdi-flag-outline',
						tone: 'blue',
					},
					{
						key: 'cod',
						label: 'Commercial Operation Date',
						value: '01 Jan 2025',
						hint: 'Full COD — post-COD tariff branch applies',
						icon: 'mdi mdi-calendar-check-outline',
						tone: 'teal',
					},
				],
				capacity: [
					{
						key: 'ipnom',
						label: 'Initial Installed Power Rating (IPNOM)',
						value: '300.00 MWac',
						hint: 'Pre-COD installed capacity used for scaling',
						icon: 'mdi mdi-lightning-bolt-outline',
						tone: 'amber',
					},
					{
						key: 'pnom',
						label: 'Nominal Installed Power Rating (PNOM)',
						value: '2,000.00 MWac',
						hint: 'Contract nominal AC capacity',
						icon: 'mdi mdi-gauge',
						tone: 'slate',
					},
				],
			}
		},
	})
</script>

<style scoped>
.project-hero {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 1rem;
	padding: 1.15rem 1.25rem;
	margin-bottom: 1.5rem;
	border-radius: 10px;
	border: 1px solid var(--surface-border);
	border-left: 4px solid var(--primary-color);
	background:
		linear-gradient(
			135deg,
			color-mix(in srgb, var(--primary-color) 8%, var(--surface-card)) 0%,
			var(--surface-50, var(--surface-ground)) 55%,
			var(--surface-card) 100%
		);
}

.project-hero__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 2.75rem;
	height: 2.75rem;
	border-radius: 9px;
	background: color-mix(in srgb, var(--primary-color) 14%, transparent);
	color: var(--primary-color);
	flex-shrink: 0;
}

.project-hero__icon i {
	font-size: 1.4rem;
}

.project-hero__copy {
	flex: 1 1 16rem;
	min-width: 0;
}

.project-hero__name {
	font-size: 1.35rem;
	font-weight: 700;
	line-height: 1.2;
	letter-spacing: -0.02em;
	color: var(--primary-color);
}

.project-hero__lead {
	margin: 0.35rem 0 0;
	max-width: 36rem;
	font-size: 0.9rem;
	line-height: 1.45;
	color: var(--text-color-secondary);
}

.project-hero__status {
	display: flex;
	flex-direction: column;
	gap: 0.2rem;
	padding: 0.65rem 0.9rem;
	border-radius: 8px;
	border: 1px solid var(--surface-border);
	background: var(--surface-card);
	min-width: 11rem;
}

.project-hero__status-label {
	font-size: 0.68rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: var(--text-color-secondary);
}

.project-hero__status-value {
	font-size: 0.9rem;
	font-weight: 650;
	color: var(--green-600, #16a34a);
}

.project-section {
	margin-bottom: 1.5rem;
}

.project-section:last-child {
	margin-bottom: 0;
}

.project-section__title {
	margin: 0 0 0.35rem;
	font-size: 1.1rem;
	font-weight: 700;
}

.project-section__lead {
	margin: 0 0 0.9rem;
	max-width: 42rem;
	font-size: 0.9rem;
	line-height: 1.5;
	color: var(--text-color-secondary);
}

.project-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 0.85rem;
}

.project-fact {
	display: flex;
	align-items: flex-start;
	gap: 0.9rem;
	padding: 1.05rem 1.1rem;
	border-radius: 10px;
	border: 1px solid var(--surface-border);
	background: var(--surface-card);
}

.project-fact__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 2.5rem;
	height: 2.5rem;
	border-radius: 9px;
	flex-shrink: 0;
}

.project-fact__icon i {
	font-size: 1.25rem;
}

.project-fact__icon--blue {
	background: color-mix(in srgb, var(--blue-500, #3b82f6) 14%, transparent);
	color: var(--blue-600, #2563eb);
}

.project-fact__icon--teal {
	background: color-mix(in srgb, var(--teal-500, #14b8a6) 14%, transparent);
	color: var(--teal-600, #0d9488);
}

.project-fact__icon--amber {
	background: color-mix(in srgb, var(--orange-500, #f59e0b) 16%, transparent);
	color: var(--orange-600, #d97706);
}

.project-fact__icon--slate {
	background: color-mix(in srgb, var(--primary-color) 12%, transparent);
	color: var(--primary-color);
}

.project-fact__body {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	min-width: 0;
}

.project-fact__label {
	font-size: 0.72rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: var(--text-color-secondary);
}

.project-fact__value {
	font-size: 1.35rem;
	font-weight: 700;
	line-height: 1.2;
	letter-spacing: -0.02em;
	word-break: break-word;
}

.project-fact__value.mono {
	font-variant-numeric: tabular-nums;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	font-size: 1.25rem;
}

.project-fact__hint {
	font-size: 0.82rem;
	line-height: 1.4;
	color: var(--text-color-secondary);
}

@media screen and (max-width: 760px) {
	.project-grid {
		grid-template-columns: 1fr;
	}

	.project-hero__name {
		font-size: 1.15rem;
	}

	.project-fact__value {
		font-size: 1.2rem;
	}
}
</style>
